import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Container,
  Grid,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { LangContext } from "../../LanguageProvider";
import InstagramFeed from "./instafeeds";
import useAuthStore from "../../services/AuthStore";
import { useNavigate } from "react-router-dom";

// --- Styled Components ---
const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 360,
  height: "420px",
  transition: "transform 0.4s ease-in-out, box-shadow 0.4s ease-in-out",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: theme.shadows[8],
    cursor: "pointer",
  },
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  height: 220,
  transition: "opacity 0.4s ease-in-out, transform 0.4s ease-in-out",
  "&:hover": {
    opacity: 0.9,
    transform: "scale(1.02)",
  },
}));

const GradientOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: "100%",
  background:
    "linear-gradient(180deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.8) 100%)",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  background: "linear-gradient(to right, #38a169, #4299e1)",
  color: theme.palette.common.white,
  "&:hover": {
    background: "linear-gradient(to right, #38a169, #4299e1)",
    transform: "scale(1.03)",
  },
}));

const formatMonthYear = (isoMonth, lang = "ru") => {
  if (!isoMonth) return "";
  const [year, month] = isoMonth.split("-");
  const dateObj = new Date(year, month - 1);
  const options = { year: "numeric", month: "long" };
  const locale = lang === "kz" ? "kk-KZ" : "ru-RU";
  const formatted = new Intl.DateTimeFormat(locale, options).format(dateObj);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

export default function News() {
  const { translations, lang } = useContext(LangContext);
  const { getNews, getNewsDates } = useAuthStore();
  const navigate = useNavigate();

  const [news, setNews] = useState([]);
  const [dateFilters, setDateFilters] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null); // YYYY-MM
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isDatesLoading, setIsDatesLoading] = useState(false);

  // 1. Load available dates (filters) on mount
  useEffect(() => {
    const fetchDates = async () => {
      setIsDatesLoading(true);
      try {
        const dates = await getNewsDates();
        setDateFilters(dates);
      } catch (error) {
        console.error("Error loading dates:", error);
      } finally {
        setIsDatesLoading(false);
      }
    };
    fetchDates();
  }, [getNewsDates]);

  // 2. Load news when page or filter changes
  useEffect(() => {
    const fetchNews = async () => {
      setIsLoading(true);
      try {
        let year = null;
        let month = null;

        if (selectedFilter) {
          const [y, m] = selectedFilter.split("-");
          year = parseInt(y);
          month = parseInt(m);
        }

        const data = await getNews(page, 3, year, month); // Limit 3 per page

        if (page === 1) {
          setNews(data.news);
        } else {
          setNews(prev => [...prev, ...data.news]);
        }
        setHasNext(data.has_next);
      } catch (error) {
        console.error("Error loading news:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNews();
  }, [page, selectedFilter, getNews]);

  // Handle filter change
  const handleFilterChange = newFilter => {
    if (selectedFilter !== newFilter) {
      setSelectedFilter(newFilter);
      setPage(1); // Reset to page 1
      setNews([]); // Clear current list
      setHasNext(true);
    }
  };

  const handleLoadMore = () => {
    if (hasNext && !isLoading) {
      setPage(prev => prev + 1);
    }
  };

  const truncateText = (text, limit) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        padding: 4,
        marginTop: 11,
      }}>
      <Typography
        variant="h4"
        gutterBottom
        align="center"
        sx={{ fontWeight: "bold", paddingTop: 5, color: "text.primary" }}>
        {translations["news"] || "Новости"}
      </Typography>

      <InstagramFeed />

      <Grid container spacing={4} sx={{ paddingTop: 5 }}>
        {/* Sidebar - Filters */}
        <Grid item xs={12} md={3}>
          <Typography variant="h6" gutterBottom sx={{ color: "text.primary" }}>
            {translations["Archive"]}
          </Typography>
          <Paper elevation={2}>
            {isDatesLoading ? (
              <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List component="nav" dense>
                <ListItemButton
                  selected={selectedFilter === null}
                  onClick={() => handleFilterChange(null)}>
                  <ListItemText primary={translations["AllNews"]} />
                </ListItemButton>

                {dateFilters.map(monthIso => (
                  <ListItemButton
                    key={monthIso}
                    selected={selectedFilter === monthIso}
                    onClick={() => handleFilterChange(monthIso)}>
                    <ListItemText primary={formatMonthYear(monthIso, lang)} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Main Content - News Grid */}
        <Grid item xs={12} md={9}>
          <Grid
            container
            spacing={4}
            justifyContent="center"
            alignItems="stretch">
            {news.length > 0
              ? news.map(newsItem => (
                  <Grid item key={newsItem.id} xs={12} sm={6} md={4}>
                    <StyledCard>
                      <Box sx={{ position: "relative" }}>
                        <StyledCardMedia
                          component="img"
                          image={
                            newsItem.images && newsItem.images.length > 0
                              ? `data:;base64,${newsItem.images[0]}`
                              : "/images/placeholder.jpg"
                          }
                          alt={newsItem.title_ru}
                        />
                        <GradientOverlay />
                      </Box>
                      <CardContent>
                        <Typography variant="h6" component="div" gutterBottom>
                          {truncateText(
                            lang === "kz"
                              ? newsItem.title_kz
                              : newsItem.title_ru,
                            30,
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 1,
                            height: 35,
                            overflow: "hidden",
                          }}>
                          {truncateText(
                            lang === "kz"
                              ? newsItem.description_kz
                              : newsItem.description_ru,
                            50,
                          )}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mt: 2,
                          }}>
                          <StyledButton
                            variant="contained"
                            onClick={() => navigate(`/news/${newsItem.id}`)}>
                            {translations["Read more"]}
                          </StyledButton>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textWrap: "wrap" }}>
                            {new Date(newsItem.public_date).toLocaleDateString(
                              "ru-RU",
                            )}
                          </Typography>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                ))
              : !isLoading && (
                  <Typography sx={{ p: 5, color: "text.secondary" }}>
                    {translations["no_news_found"] ||
                      "По этому фильтру новостей не найдено."}
                  </Typography>
                )}
          </Grid>

          {/* Loading Indicator & Load More Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              hasNext && (
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  sx={{ minWidth: "200px" }}>
                  {translations["load_more"] || "Загрузить еще"}
                </Button>
              )
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}
